import { RefObject, useCallback, useEffect, useRef, useState } from "react";

import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import {
	clearInbox,
	listSelector,
	setCurrentLeadIndex,
} from "@/pages/Private/redux/list/list.slice";
import { InclusionFilterStatus, OutboxTab, PriorityFilterStatus } from "@/enum/list.enum";
import { useLazyGetAllProspectsQuery } from "@/pages/Private/redux/list/list.api";

import { DropdownWithCheckboxesItem } from "@/components/DropdownWithCheckboxes/DropdownWithCheckboxes";

import { SortParams } from "@/types/types";

import { IFilterWithValues } from "../../../List/components/FilterModal";
import { useLeadsPagingSentinel } from "../../utils/useLeadsPagingSentinel";
import { CustomListsAutocompleteForSequencerItem } from "../../../List/components/CustomListsAutocompleteForSequencer";
import { ALL_SENDERS_ID } from "./outboxSenderId";
import {
	LIST_SCOPE_RELEVANCE_STATUSES,
	ListScope,
} from "../../components/ListScopeFilter/listScope";

interface UseOutboxProspectsArgs {
	/** The first request waits for the sending users so the sender default can settle. */
	sendingUsersLoaded: boolean;
	filter: IFilterWithValues | undefined;
	type: string;
	query: string;
	limit: number;
	selectedSenders: DropdownWithCheckboxesItem[];
	selectedLists: CustomListsAutocompleteForSequencerItem[];
	/** Status-tab scope: the active tab's bucket(s) — memoized by the caller. */
	outboxTabs: OutboxTab[];
	/** Campaign filter uuids; empty = no narrowing. */
	campaignIds: string[];
	/** Chat/campaign source scope; undefined = no narrowing. */
	outboxSource: "chat" | "campaign" | undefined;
	/** Resolved sort (per-tab default vs explicit user sort). */
	sorting: SortParams | undefined;
}

/**
 * The assignStatus population every Outbox request uses. Read from `listScope.ts`, the one module
 * that owns "which statuses live on which surface" — the same set every per-lead deep link into
 * this page is built from, so a link can never name a lead this request would not return.
 * Mirrors the API's `OUTBOX_ASSIGN_STATUSES`.
 */
const OUTBOX_ASSIGN_STATUSES = LIST_SCOPE_RELEVANCE_STATUSES[ListScope.OUTBOX];

interface OutboxProspects {
	page: number;
	resetPage: () => void;
	/** Sentinel node at the bottom of the leads column that triggers loading the next page. */
	leadsEndRef: RefObject<HTMLDivElement>;
	isFetching: boolean;
	isLoading: boolean;
	hasMorePages: boolean;
}

/**
 * Owns the Outbox `getAllProspects` request cycle: the filter set → request mapping,
 * page-1 resets when any filter changes, and the infinite scroll that advances the
 * page when the leads column's sentinel becomes visible. Results land in the shared
 * `list` slice (with its request-id guard); components read them from there.
 */
export const useOutboxProspects = ({
	sendingUsersLoaded,
	filter,
	type,
	query,
	limit,
	selectedSenders,
	selectedLists,
	outboxTabs,
	campaignIds,
	outboxSource,
	sorting,
}: UseOutboxProspectsArgs): OutboxProspects => {
	const dispatch = useAppDispatch();
	const { inbox, meta } = useAppSelector(listSelector);

	const [getAllProspects, { isFetching, isLoading }] = useLazyGetAllProspectsQuery();

	const [page, setPage] = useState<number>(1);
	const leadsEndRef = useRef<HTMLDivElement>(null);

	const resetPage = useCallback(() => setPage(1), []);

	const loadMoreData = useCallback(() => {
		if (!isFetching && page < (meta?.pageCount || 0) && page === meta.page) {
			setPage(page + 1);
		}
	}, [page, isFetching, meta]);

	useLeadsPagingSentinel({ items: inbox, sentinelRef: leadsEndRef, onLoadMore: loadMoreData });

	// Clear inbox and reset to page 1 when filters/search/sorting change (but not when only page changes)
	useEffect(() => {
		setPage(1);
	}, [
		dispatch,
		filter,
		type,
		query,
		sorting,
		selectedSenders,
		selectedLists,
		outboxTabs,
		campaignIds,
		outboxSource,
	]);

	// Fetch data when any parameter changes (including page)
	useEffect(() => {
		// Wait for sendingUsers to load before firing the first request
		// to avoid the request being canceled by a subsequent re-trigger
		// when selectedSenders gets updated after sendingUsers loads
		if (!sendingUsersLoaded) {
			return;
		}

		// Only clear inbox on fresh loads (page 1), not when paginating
		if (page === 1) {
			dispatch(clearInbox());
			dispatch(setCurrentLeadIndex(0));
		}

		// No manual abort here: a stale response (e.g. the previous tab's request landing after a
		// client-side tab switch) is ignored by the slice's request-id guard (see list-inbox.helper).
		getAllProspects({
			page,
			limit,
			searchValue: query,
			sorting,
			assignStatus: OUTBOX_ASSIGN_STATUSES,
			outboxTabs: !query ? outboxTabs : undefined,
			campaignIds: !query && campaignIds.length > 0 ? campaignIds : undefined,
			outboxSource: !query ? outboxSource : undefined,
			filter: !query ? filter : undefined,
			inclusionFilterStatus: [InclusionFilterStatus.ADDED],
			priorityFilterStatus: [PriorityFilterStatus.ALL],
			senders:
				(!query &&
					selectedSenders
						.filter((sender) => sender.id !== ALL_SENDERS_ID)
						.map((sender) => sender.id as number)) ||
				[],
			listIds:
				selectedLists?.length > 0 && !query
					? [...(selectedLists?.map((list) => list.id as number) || [])]
					: undefined,
		});
	}, [
		sendingUsersLoaded,
		filter,
		getAllProspects,
		type,
		limit,
		page,
		query,
		sorting,
		selectedSenders,
		selectedLists,
		outboxTabs,
		campaignIds,
		outboxSource,
	]);

	return {
		page,
		resetPage,
		leadsEndRef,
		isFetching,
		isLoading,
		hasMorePages: !!meta && page < meta.pageCount,
	};
};
